/**
 * POSTCARD CHECKOUT GUARD
 *
 * If a postcard line item is in the cart with an empty 'Personlig Besked'
 * property when the user clicks 'Checkout', show a confirmation modal
 * asking them to either go back and write a greeting, or continue
 * without one.
 *
 * Scope: standard cart-form checkout buttons only (cart drawer + cart page).
 * Does NOT intercept Shop Pay / Apple Pay / Google Pay accelerated buttons
 * (those bypass the cart form entirely).
 *
 * Fail-open: if the cart fetch fails for any reason, checkout is allowed
 * to proceed normally rather than blocking the sale.
 *
 * FIX (2026-05-13): Replaced button.click() with submitForm() which uses
 * form.requestSubmit(button) with a reliable fallback. The old button.click()
 * approach was called from inside a Promise.then() callback (async context),
 * which caused silent failures in some browsers.
 */

(function () {
  'use strict';

  const PROP_MESSAGE = 'Personlig Besked';
  const PROP_FLAG = '_postcard';
  const STYLE_ID = 'postcard-checkout-guard-styles';

  let modalEl = null;
  let pendingForm = null;
  let pendingButton = null;

  // ============================================
  // Reliable form submission helper
  // Replaces button.click() which can fail silently
  // when called from async (Promise.then) context
  // ============================================
  function submitForm(form, button) {
    if (!form) return;
    try {
      // requestSubmit(button) includes name="checkout" in the submission
      // and triggers form validation — preferred over form.submit()
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(button || null);
        return;
      }
    } catch (e) {
      // requestSubmit can throw if button is not a submit button of this form
      // fall through to fallback
    }
    // Fallback: manually add hidden checkout input and submit
    try {
      if (button && button.name) {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = button.name;
        hidden.value = button.value || '';
        form.appendChild(hidden);
        form.submit();
        // Note: hidden input stays in DOM briefly but page navigates away
      } else {
        form.submit();
      }
    } catch (e2) {
      console.error('[Postcard guard] form submit failed', e2);
    }
  }

  // ============================================
  // Inject styles (idempotent)
  // ============================================
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .pcg-modal {
        position: fixed;
        inset: 0;
        z-index: 10000;
        display: none;
        align-items: center;
        justify-content: center;
        padding: 1rem;
      }
      .pcg-modal.is-open { display: flex; }
      .pcg-modal__backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
      }
      .pcg-modal__dialog {
        position: relative;
        background: #fff;
        border-radius: 12px;
        max-width: 480px;
        width: 100%;
        padding: 2rem;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        animation: pcg-fade 0.2s ease;
      }
      @keyframes pcg-fade {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .pcg-modal__title {
        font-family: var(--heading-font-family, inherit);
        font-size: 1.5rem;
        font-weight: 600;
        margin: 0 0 0.75rem;
        line-height: 1.2;
      }
      .pcg-modal__body {
        font-size: 0.9375rem;
        line-height: 1.5;
        margin: 0 0 1.5rem;
        color: rgb(var(--text-color, 28 28 28) / 0.75);
      }
      .pcg-modal__actions {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .pcg-modal__btn {
        appearance: none;
        font-family: inherit;
        font-size: 0.9375rem;
        font-weight: 500;
        padding: 0.875rem 1.5rem;
        border-radius: 0.5rem;
        cursor: pointer;
        letter-spacing: 0.02em;
        transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        text-align: center;
        line-height: 1.2;
      }
      .pcg-modal__btn--back {
        background: rgb(var(--text-color, 28 28 28));
        color: rgb(var(--background, 255 255 255));
        border: 1px solid rgb(var(--text-color, 28 28 28));
      }
      .pcg-modal__btn--back:hover {
        background: rgb(var(--text-color, 28 28 28) / 0.85);
        border-color: rgb(var(--text-color, 28 28 28) / 0.85);
      }
      .pcg-modal__btn--continue {
        background: transparent;
        color: rgb(var(--text-color, 28 28 28) / 0.65);
        border: 1px solid rgb(var(--text-color, 28 28 28) / 0.2);
      }
      .pcg-modal__btn--continue:hover {
        color: rgb(var(--text-color, 28 28 28));
        border-color: rgb(var(--text-color, 28 28 28) / 0.4);
      }
      @media (min-width: 600px) {
        .pcg-modal__actions { flex-direction: row-reverse; }
        .pcg-modal__btn { flex: 1; }
      }
    `;
    document.head.appendChild(style);
  }

  // ============================================
  // Cart-state check
  // ============================================
  async function findPostcardWithMissingMessage() {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store'
      });
      if (!response.ok) return null;
      const cart = await response.json();

      const items = cart.items || [];
      for (const item of items) {
        const props = item.properties || {};
        const isPostcard = props[PROP_FLAG] === 'true' || props[PROP_FLAG] === true;
        if (!isPostcard) continue;

        const raw = props[PROP_MESSAGE];
        const message = raw != null ? String(raw).trim() : '';
        if (message.length === 0) {
          return item;
        }
      }
      return null;
    } catch (err) {
      console.error('[Postcard guard] cart fetch failed', err);
      return null;
    }
  }

  // ============================================
  // Modal
  // ============================================
  function ensureModal() {
    if (modalEl) return modalEl;
    injectStyles();

    modalEl = document.createElement('div');
    modalEl.className = 'pcg-modal';
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.setAttribute('aria-hidden', 'true');
    modalEl.setAttribute('aria-labelledby', 'pcg-modal-title');
    modalEl.innerHTML =
      '<div class="pcg-modal__backdrop" data-pcg-dismiss></div>' +
      '<div class="pcg-modal__dialog" role="document">' +
      '<h2 id="pcg-modal-title" class="pcg-modal__title">Du har ikke skrevet en hilsen</h2>' +
      '<p class="pcg-modal__body">Du har et postkort i kurven, men ikke skrevet en personlig besked. Vil du fortsætte uden hilsen, eller gå tilbage og skrive en?</p>' +
      '<div class="pcg-modal__actions">' +
      '<button type="button" class="pcg-modal__btn pcg-modal__btn--back" data-pcg-back>Tilbage og skriv hilsen</button>' +
      '<button type="button" class="pcg-modal__btn pcg-modal__btn--continue" data-pcg-continue>Fortsæt uden besked</button>' +
      '</div>' +
      '</div>';
    document.body.appendChild(modalEl);

    modalEl.querySelectorAll('[data-pcg-dismiss]').forEach((el) => {
      el.addEventListener('click', closeModal);
    });
    modalEl.querySelector('[data-pcg-back]').addEventListener('click', onBack);
    modalEl.querySelector('[data-pcg-continue]').addEventListener('click', onContinue);

    return modalEl;
  }

  function openModal() {
    ensureModal();
    modalEl.classList.add('is-open');
    modalEl.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    const backBtn = modalEl.querySelector('[data-pcg-back]');
    if (backBtn) setTimeout(() => backBtn.focus(), 50);

    document.addEventListener('keydown', onKeyDown);
  }

  function closeModal() {
    if (!modalEl) return;
    modalEl.classList.remove('is-open');
    modalEl.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeyDown);
    pendingForm = null;
    pendingButton = null;
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
    }
  }

  function onBack() {
    closeModal();

    const editor = document.querySelector('postcard-message-editor');
    if (!editor) return;

    const toggle = editor.querySelector('.postcard-message-editor__toggle');
    const content = editor.querySelector('.postcard-message-editor__content');
    const textarea = editor.querySelector('textarea');

    if (toggle && content && content.hidden) {
      try { toggle.click(); } catch (_) {}
    }

    setTimeout(() => {
      try {
        editor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) {}
      if (textarea) {
        try { textarea.focus(); } catch (_) {}
      }
    }, 150);
  }

  function onContinue() {
    const formToSubmit = pendingForm;
    const buttonToClick = pendingButton;
    closeModal();
    if (formToSubmit) {
      submitForm(formToSubmit, buttonToClick);
    }
  }

  // ============================================
  // Click interception (capture phase)
  // ============================================
  function onCheckoutClick(e) {
    if (e.button !== undefined && e.button !== 0) return;

    const button = e.target.closest('button[name="checkout"]');
    if (!button) return;

    const form = button.closest('form');
    if (!form) return;

    const action = form.getAttribute('action') || '';
    if (action.indexOf('/cart') === -1) return;

    // Prevent re-entry: if already processing, do nothing
    if (form.dataset.pcgProcessing === 'true') return;

    e.preventDefault();
    e.stopPropagation();

    form.dataset.pcgProcessing = 'true';

    findPostcardWithMissingMessage().then((item) => {
      form.dataset.pcgProcessing = 'false';
      if (item) {
        pendingForm = form;
        pendingButton = button;
        openModal();
      } else {
        // No missing message — submit directly, no re-click needed
        submitForm(form, button);
      }
    }).catch((err) => {
      // Fail-open: never block checkout on a JS error
      console.error('[Postcard guard] check failed, allowing checkout', err);
      form.dataset.pcgProcessing = 'false';
      submitForm(form, button);
    });
  }

  // ============================================
  // Init
  // ============================================
  function init() {
    document.addEventListener('click', onCheckoutClick, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
